import React from 'react'
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    SortableContext,
    useSortable,
    arrayMove,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const FileReorder = ({
    props,
    formik,
    removeFile,
    viewFile,
    handleImageEditModal,
    handleImageRotate,
}) => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 100,
                tolerance: 5,
            },
        })
    )

    const onDragEnd = (event) => {
        const { active, over } = event

        if (over && active.id !== over.id) {
            const from = formik.values[props.name].findIndex(
                (file) => file === active.id
            )
            const to = formik.values[props.name].findIndex(
                (file) => file === over.id
            )

            if (from !== -1 && to !== -1) {
                const reorderedItems = arrayMove(
                    formik.values[props.name],
                    from,
                    to
                )
                formik.setFieldValue(props.name, reorderedItems)
            }
        }
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
        >
            <SortableContext
                items={formik.values[props.name]}
                strategy={verticalListSortingStrategy}
            >
                <div className="reorderCnt">
                    {formik.values[props.name]?.map((file, index) => (
                        <SortableItem
                            key={file}
                            id={file}
                            file={file}
                            index={index}
                            props={props}
                            removeFile={removeFile}
                            viewFile={viewFile}
                            handleImageEditModal={handleImageEditModal}
                            handleImageRotate={handleImageRotate}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    )
}

const SortableItem = ({
    id,
    file,
    index,
    props,
    removeFile,
    viewFile,
    handleImageEditModal,
    handleImageRotate,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
    }

    const getFileExtension = (fileName) =>
        fileName.split('.').pop().toLowerCase()
    const isValidHttpUrl = (url) => {
        try {
            new URL(url)
            return true
        } catch {
            return false
        }
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="thumb"
        >
            <div className="thumbInner">
                {['png', 'jpg', 'webp', 'jpeg'].includes(
                    getFileExtension(file)
                ) ? (
                    <div
                        className={`thumbCnt ${
                            props.disableRotate ? 'noRotateButtons' : ''
                        }`}
                    >
                        {isValidHttpUrl(file) ? (
                            <img
                                src={file}
                                className="img"
                                id={`thumb_${index}`}
                                alt="File Thumbnail"
                            />
                        ) : (
                            <img
                                src={`${
                                    global.customImgurl
                                        ? `${
                                              process.env.REACT_APP_IMAGE_URL
                                          }/uploads/${
                                              props.isCompress &&
                                              props.isCompress !== 'false'
                                                  ? `${props.folder}/compressed`
                                                  : props.folder
                                          }/${file}?t=${Date.now()}`
                                        : `${global.site_url}/uploads/${
                                              props.isCompress &&
                                              props.isCompress !== 'false'
                                                  ? `${props.folder}/compressed`
                                                  : props.folder
                                          }/${file}?t=${Date.now()}`
                                }`}
                                className="img"
                                id={`thumb_${index}`}
                                alt="File Thumbnail"
                            />
                        )}

                        {/* Top Rotate Buttons - only show if not disabled */}
                        {!props.disableRotate && (
                            <div
                                className="fileActionsTop"
                                onPointerDown={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleImageRotate(file, index, -90)
                                    }}
                                    className="rotateBtn material-icons"
                                    title="Rotate Left 90°"
                                >
                                    rotate_left
                                </span>
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleImageRotate(file, index, 90)
                                    }}
                                    className="rotateBtn material-icons"
                                    title="Rotate Right 90°"
                                >
                                    rotate_right
                                </span>
                            </div>
                        )}

                        {/* Bottom Action Buttons */}
                        <div
                            className="fileActions"
                            onPointerDown={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            {!props.disableDeleting && (
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        removeFile(file, props.name, index)
                                    }}
                                    className="cancelBtn material-icons"
                                    title="Delete"
                                >
                                    delete
                                </span>
                            )}
                            <span
                                onClick={(e) => {
                                    e.stopPropagation()
                                    viewFile(file, props.name)
                                }}
                                className="viewBtn material-icons"
                                title="View"
                            >
                                visibility
                            </span>

                            {!props.disableEditing && (
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleImageEditModal(true, file, index)
                                    }}
                                    className="editBtn material-icons"
                                    title="Edit"
                                >
                                    edit
                                </span>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="thumbCnt">
                        <div className="defaultThumb">
                            <span className="material-icons">
                                {getFileExtension(file) === 'pdf'
                                    ? 'picture_as_pdf'
                                    : ['doc', 'docx'].includes(
                                            getFileExtension(file)
                                        )
                                      ? 'description'
                                      : ['mp4', 'mpeg'].includes(
                                              getFileExtension(file)
                                          )
                                        ? 'movie'
                                        : ['mpga', 'mp3'].includes(
                                                getFileExtension(file)
                                            )
                                          ? 'volume_up'
                                          : 'description'}
                            </span>
                        </div>
                        <div
                            className="fileActions"
                            onPointerDown={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            {!props.disableDeleting && (
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        removeFile(file, props.name, index)
                                    }}
                                    className="cancelBtn material-icons"
                                    title="Delete"
                                >
                                    delete
                                </span>
                            )}
                            <span
                                onClick={(e) => {
                                    e.stopPropagation()
                                    viewFile(file, props.name)
                                }}
                                className="viewBtn material-icons"
                                title="View"
                            >
                                visibility
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default FileReorder
